import { ChangeEvent, ReactElement, useCallback, useMemo, useState } from 'react'
import { useIsOrganisationAdmin } from '@hooks'
import chunk from 'lodash-es/chunk'
import {
  Alert,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import type { ProjectShearConnectionUsageDTO, ShearConnectionDTO } from '@api/fastAPI.schemas'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import {
  SHEAR_SHORT_NAME_PREFIX,
  getConnectionShortNameForCopy,
  getDefaultConnectionShortName,
} from '../../connections/utils/shortNames'
import CreateShearConnectionDialog from '../CreateShearConnectionDialog'
import EditShearConnectionDialog from '../EditShearConnectionDialog'
import PasteShearConnectionFromClipboardButton from '../PasteShearConnectionFromClipboardButton'
import { shearConnectionSchema } from '../ShearConnectionForm/schema'
import ShearConnectionListItem from '../ShearConnectionListItem'
import {
  preparePastedShearConnection,
  serializeShearConnectionForClipboard,
} from '../utils/clipboardShearConnection'

interface Props {
  connections: ShearConnectionDTO[]
  onDelete: (connection: ShearConnectionDTO) => void
  onCreate: (connection: ShearConnectionDTO) => Promise<unknown>
  onEdit: (connection: ShearConnectionDTO) => Promise<unknown>
  isCreating: boolean
  isEditing: boolean
  /** Project libraries are editable by every member; only the organisation library is admin-only. */
  isProjectLibrary?: boolean
  defaultRowsPerPage?: 5 | 10 | 25
  /** How many Schubanker use each Schubverbinder; shows the usage column when given. */
  usageByGuid?: Record<string, ProjectShearConnectionUsageDTO>
}

const ShearConnectionList = ({
  connections,
  onDelete,
  onCreate,
  onEdit,
  isCreating,
  isEditing,
  isProjectLibrary = false,
  defaultRowsPerPage = 25,
  usageByGuid,
}: Props): ReactElement => {
  // The organisation library belongs to the organisation: every member sees it,
  // only an admin may change it. Assume read-only until we know otherwise, so
  // no control is offered that the backend would refuse.
  const { isOrganisationAdmin } = useIsOrganisationAdmin()
  const { enqueueSnackbar } = useSnackbar()
  const readOnly = !isProjectLibrary && !isOrganisationAdmin
  const [createValues, setCreateValues] = useState<ShearConnectionDTO | null>(null)
  const [editValues, setEditValues] = useState<ShearConnectionDTO | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultRowsPerPage)

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleCopy = useCallback(
    (connection: ShearConnectionDTO) => {
      try {
        navigator.clipboard.writeText(serializeShearConnectionForClipboard(connection))
        enqueueSnackbar('Schubverbinder in Zwischenablage kopiert', { variant: 'success' })
      } catch {
        enqueueSnackbar('Fehler beim Kopieren des Schubverbinders', { variant: 'error' })
      }
    },
    [enqueueSnackbar],
  )

  // The pasted copy opens in the create dialog, like a new entry, so it can be
  // checked and renamed before it is saved.
  // A copy keeps its short name only while no Schubverbinder here uses it yet.
  const handlePaste = useCallback(
    (connection: ShearConnectionDTO) => {
      setCreateValues({
        ...connection,
        short_name: getConnectionShortNameForCopy(connection, connections, SHEAR_SHORT_NAME_PREFIX),
      })
    },
    [connections],
  )

  // A duplicate is the copy a paste produces -- new guid, "(Kopie)" name -- opened
  // in the create dialog so it can be renamed before it is saved.
  const handleDuplicate = useCallback(
    (connection: ShearConnectionDTO) => {
      handlePaste(preparePastedShearConnection(connection))
    },
    [handlePaste],
  )

  const allShortNames = useMemo(
    () => connections.map(connection => connection.short_name),
    [connections],
  )
  // While editing, the Schubverbinder's own short name is not taken.
  const shortNamesTakenByOthers = useMemo(
    () =>
      connections
        .filter(connection => connection.guid !== editValues?.guid)
        .map(connection => connection.short_name),
    [connections, editValues],
  )

  const chunks = useMemo(() => chunk(connections, rowsPerPage), [connections, rowsPerPage])
  const connectionsInPage = chunks[page] || chunks[page - 1] || []

  const hasConnections = !!connections.length

  const createButton = (
    <Button
      onClick={() =>
        setCreateValues({
          ...(shearConnectionSchema.getDefault() as ShearConnectionDTO),
          short_name: getDefaultConnectionShortName(connections, SHEAR_SHORT_NAME_PREFIX),
        })
      }
      variant="outlined"
      color="primary"
      size="small"
      sx={{ alignSelf: 'center' }}
      data-cy="create-shear-connection"
    >
      anlegen
    </Button>
  )

  const createActions = (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
      <PasteShearConnectionFromClipboardButton onPaste={handlePaste} />
      {createButton}
    </Stack>
  )

  return (
    <>
      <Box>
        <Alert severity="info" sx={{ py: 0, mb: 2 }} data-cy="shear-connection-resistance-hint">
          Geben Sie den Widerstand eines einzelnen Schubverbinders und den Abstand an, in dem er
          eingebaut wird. Nachgewiesen wird der Schubfluss der Wand gegen R<sub>d</sub> / Abstand.
        </Alert>
        <Box display="flex" mb={1} justifyContent="space-between">
          <Typography variant="h6" mb={1}>
            Schubverbinder {`(${connections.length})`}
          </Typography>
          {hasConnections && !readOnly && createActions}
        </Box>
        {!hasConnections && (
          <Paper>
            <Box padding={3} textAlign="center">
              <Typography>
                {readOnly
                  ? 'Es sind noch keine Schubverbinder angelegt'
                  : 'Sie haben noch keine Schubverbinder angelegt'}
              </Typography>
              {!readOnly && <Box mt={2}>{createActions}</Box>}
            </Box>
          </Paper>
        )}
        {hasConnections && (
          <>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="Schubverbinder">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Kurzbezeichnung</TableCell>
                    <TableCell>Kommentar im Export</TableCell>
                    <TableCell>Abstand (m)</TableCell>
                    <TableCell>
                      <>
                        R<sub>d</sub> Erdbeben (kN)
                      </>
                    </TableCell>
                    <TableCell>
                      <>
                        R<sub>d</sub> Wind (kN)
                      </>
                    </TableCell>
                    {usageByGuid && <TableCell>Verwendet (davon manuell)</TableCell>}
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {connectionsInPage.map(connection => (
                    <ShearConnectionListItem
                      key={connection.guid}
                      connection={connection}
                      isProjectLibrary={isProjectLibrary}
                      usage={usageByGuid?.[connection.guid]}
                      showUsage={!!usageByGuid}
                      onDelete={() => onDelete(connection)}
                      onEdit={() => setEditValues(connection)}
                      onCopy={() => handleCopy(connection)}
                      onDuplicate={() => handleDuplicate(connection)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Schubverbinder pro Seite"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
              }
              component="div"
              count={connections.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Box>
      {createValues && (
        <CreateShearConnectionDialog
          open
          connection={createValues}
          takenShortNames={allShortNames}
          isSaving={isCreating}
          onSave={onCreate}
          onClose={() => setCreateValues(null)}
        />
      )}
      {editValues && (
        <EditShearConnectionDialog
          open
          connection={editValues}
          takenShortNames={shortNamesTakenByOthers}
          isSaving={isEditing}
          onSave={onEdit}
          onClose={() => setEditValues(null)}
        />
      )}
    </>
  )
}

export default ShearConnectionList
